import { useEffect, useState } from 'react'
import { dailyReportAPI, marketingAPI, trafficAPI } from '../services/api'
import { averageTraffic } from '../lib/channelReport'

export function useChannelReport({ clinicId, start, end, days = null, until = null, scope, lookbackDays, attributionWindow, accountingMode, refresh, enabled = true }) {
  const key = JSON.stringify([clinicId, start, end, days, until, scope, lookbackDays, attributionWindow, accountingMode, refresh, enabled])
  const [state, setState] = useState({ key: null, data: null, error: null })
  useEffect(() => {
    if (!enabled || !clinicId) return
    let active = true
    const request = dailyReportAPI.getAttributionModels({
      ...(days ? { days: days.join(',') } : { startDate: start, endDate: end }),
      ...(until ? { until } : {}),
      scope, lookbackDays, attributionWindow, accountingMode,
    })
    request.then(response => {
      if (active) setState({ key, data: response.data.data, error: null })
    }).catch(error => {
      if (active) setState({ key, data: null, error: error.response?.data?.error?.message || 'Could not load channel data. Try refreshing the report.' })
    })
    return () => { active = false }
    // Keyed on the serialized request: `days` is a fresh array each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  if (!enabled || !clinicId) return { data: null, error: null, loading: false }
  return state.key === key ? { ...state, loading: false } : { data: null, error: null, loading: true }
}

/**
 * First-touch traffic and ad clicks per channel; independent of the engine.
 * `until` (HH:MM ET) cuts a single day at that time.
 */
/**
 * The newest pageview the clinic's BigQuery holds (`current_through`, ISO, or
 * null when unknown). Refetched with the report.
 */
export function useDataFreshness({ clinicId, refresh, enabled = true }) {
  const key = JSON.stringify([clinicId, refresh, enabled])
  const [state, setState] = useState({ key: null, data: null })
  useEffect(() => {
    if (!enabled || !clinicId) return
    let active = true
    trafficAPI.getDataFreshness()
      .then(response => { if (active) setState({ key, data: response.data.data }) })
      .catch(() => { if (active) setState({ key, data: { current_through: null } }) })
    return () => { active = false }
  }, [key, enabled, clinicId])
  if (!enabled || !clinicId) return { data: null, loading: false }
  return state.key === key ? { data: state.data, loading: false } : { data: null, loading: true }
}

export function useChannelTraffic({ clinicId, start, end, until = null, refresh, enabled = true }) {
  const key = JSON.stringify([clinicId, start, end, until, refresh, enabled])
  const [state, setState] = useState({ key: null, data: null, error: null })
  useEffect(() => {
    if (!enabled || !clinicId) return
    let active = true
    trafficAPI.getChannelTraffic({ start, end, ...(until ? { until } : {}) }).then(response => {
      if (active) setState({ key, data: response.data.data, error: null })
    }).catch(error => {
      if (active) setState({ key, data: null, error: error.response?.data?.error?.message || 'Could not load traffic data.' })
    })
    return () => { active = false }
  }, [key, enabled, clinicId, start, end, until])
  if (!enabled || !clinicId) return { data: null, error: null, loading: false }
  return state.key === key ? { ...state, loading: false } : { data: null, error: null, loading: true }
}

/**
 * Traffic for a set of single days, each cut at `until` (HH:MM ET) when given,
 * averaged per day. The traffic endpoint cuts one day at a time, so a 7-day
 * baseline is seven requests; they run together.
 */
export function useChannelTrafficDays({ clinicId, days, until = null, refresh, enabled = true }) {
  const key = JSON.stringify([clinicId, days, until, refresh, enabled])
  const [state, setState] = useState({ key: null, data: null, error: null })
  useEffect(() => {
    if (!enabled || !clinicId || !days?.length) return
    let active = true
    const [, dayList, cut] = JSON.parse(key)
    Promise.all(dayList.map(day => trafficAPI.getChannelTraffic({ start: day, end: day, ...(cut ? { until: cut } : {}) })
      .then(response => response.data.data)))
      .then(responses => { if (active) setState({ key, data: averageTraffic(responses), error: null }) })
      .catch(error => {
        if (active) setState({ key, data: null, error: error.response?.data?.error?.message || 'Could not load traffic data.' })
      })
    return () => { active = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  if (!enabled || !clinicId || !days?.length) return { data: null, error: null, loading: false }
  return state.key === key ? { ...state, loading: false } : { data: null, error: null, loading: true }
}

/**
 * The selected model's credit by campaign → content within each channel.
 * Fetched only once someone opens a channel row: it re-reads every journey.
 */
export function useChannelCampaigns({ clinicId, start, end, engine, scope, lookbackDays, attributionWindow, accountingMode, refresh, enabled = true }) {
  const key = JSON.stringify([clinicId, start, end, engine, scope, lookbackDays, attributionWindow, accountingMode, refresh, enabled])
  const [state, setState] = useState({ key: null, data: null, error: null })
  useEffect(() => {
    if (!enabled || !clinicId) return
    let active = true
    marketingAPI.getChannelCampaignAttribution({ startDate: start, endDate: end, engine, scope, lookbackDays, attributionWindow, accountingMode }).then(response => {
      if (active) setState({ key, data: response.data.data, error: null })
    }).catch(error => {
      if (active) setState({ key, data: null, error: error.response?.data?.error?.message || 'Could not load campaigns.' })
    })
    return () => { active = false }
  }, [key, enabled, clinicId, start, end, engine, scope, lookbackDays, attributionWindow, accountingMode])
  if (!enabled || !clinicId) return { data: null, error: null, loading: false }
  return state.key === key ? { ...state, loading: false } : { data: null, error: null, loading: true }
}
